import { Maybe } from '@codegrade/maybe';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
/**
 * Create an empty state for the notifications store.
 *
 * @returns An empty `NotificationState`.
 */
function makeInitialState() {
    return {
        notifications: {
            data: [],
            pagination: { tag: 'not-started' },
        },
        hasRetrievedUnread: false,
        unreadCount: Maybe.empty(),
    };
}
const moduleBuilder = storeBuilder.module('notification', makeInitialState());
/** The Notification store module. */
export var NotificationStore;
(function (NotificationStore) {
    /** Commits the paginated notifications state. */
    NotificationStore.commitNotifications = moduleBuilder.commit((state, { notifications, }) => {
        if (notifications.data != null) {
            state.notifications = {
                ...notifications,
                data: Array.from(utils.itertools.unique(n => n.id, notifications.data)),
            };
        }
        else {
            state.notifications = notifications;
        }
    }, 'commitNotifications');
    const commitHasRetrievedAllUnead = moduleBuilder.commit((state, value) => {
        utils.vueSet(state, 'hasRetrievedUnread', value);
    }, 'commitHasRetrievedAllUnead');
    /** Commits the unread count. */
    NotificationStore.commitUpdateHasUnread = moduleBuilder.commit((state, { unreadCount, }) => {
        state.unreadCount = Maybe.of(unreadCount);
    }, 'commitUpdateHasUnread');
    /** Commits a single notification update (for read status changes). */
    const commitNotificationUpdate = moduleBuilder.commit((state, { notification, }) => {
        const data = state.notifications.data ?? [];
        const index = data.findIndex(n => n.id === notification.id);
        if (index !== -1) {
            NotificationStore.commitNotifications({
                notifications: {
                    ...state.notifications,
                    data: [...data.slice(0, index), notification, ...data.slice(index + 1)],
                },
            });
        }
    }, 'commitSingleNotification');
    /** Clear the entire store. */
    NotificationStore.commitClearNotifications = moduleBuilder.commit(state => {
        Object.assign(state, makeInitialState());
    }, 'commitClearNotifications');
    /** Gets the paginated list of notifications. */
    NotificationStore.getNotifications = moduleBuilder.read(
    /**
     * @param storeState The notification store state.
     * @returns A function that filters notifications based on unread status.
     */
    storeState => ({ unreadOnly, }) => {
        const state = utils.getPaginatedState(storeState.notifications);
        if (unreadOnly) {
            return {
                state: storeState.hasRetrievedUnread ? 'loaded-all' : state.state,
                data: state.data?.filter(val => !val.read),
            };
        }
        return state;
    }, 'getNotifications');
    /** Checks if there are any unread notifications. */
    NotificationStore.getHasUnreadNotifications = moduleBuilder.read(state => state.unreadCount.orDefault(0) > 0, 'getHasUnreadNotifications');
    /** Gets the unread count (of those in the store). */
    NotificationStore.getUnreadCount = moduleBuilder.read(state => state.unreadCount, 'getUnreadCount');
    /** Resets only the notification data and pagination state. */
    NotificationStore.commitClearData = moduleBuilder.commit(state => {
        state.notifications = makeInitialState().notifications;
        state.hasRetrievedUnread = false;
    }, 'commitClearData');
    /** Load the notification summary (unread count only). */
    NotificationStore.loadHasUnread = moduleBuilder.dispatch(async () => {
        const response = await client.p.notification.getSummary();
        NotificationStore.commitUpdateHasUnread(response.body);
    }, 'loadHasUnread');
    /** Fetches and stores a paginated list of notifications. */
    NotificationStore.loadNotifications = moduleBuilder.dispatch(async ({ state }, { toLoad, }) => {
        await Promise.all([
            utils.loadPaginatedWithForce({
                getData: () => state.notifications,
                commitData: notifications => {
                    NotificationStore.commitNotifications({ notifications });
                },
                load: async (nextToken) => {
                    const [data] = await Promise.all([
                        client.p.notification.getAll({
                            nextToken,
                        }),
                        nextToken == null ? NotificationStore.loadHasUnread() : Promise.resolve(),
                    ]);
                    if (!state.hasRetrievedUnread) {
                        commitHasRetrievedAllUnead(data.data.some(value => value.read));
                    }
                    return data;
                },
                toLoad,
            }),
        ]);
    }, 'loadNotifications');
    /**
     * Helper for update the read count.
     *
     * @param currentUnreadCount The current unread count.
     * @param state The current store state.
     */
    async function updateUnreadCountAfterRead(currentUnreadCount, state) {
        await state.unreadCount.caseOf({
            Just: async (unreadCount) => {
                if (currentUnreadCount >= 10) {
                    await NotificationStore.loadHasUnread();
                }
                else {
                    NotificationStore.commitUpdateHasUnread({
                        unreadCount: Math.max(0, unreadCount - currentUnreadCount),
                    });
                }
            },
            Nothing: () => NotificationStore.loadHasUnread(),
        });
    }
    /** Mark a notification as read. */
    NotificationStore.markAsRead = moduleBuilder.dispatch(async ({ state }, { notification, }) => {
        const res = await client.p.notification.patch({
            notificationId: notification.id,
            requestBody: { read: true },
        });
        commitNotificationUpdate({ notification: res.cgResult });
        const currentUnreadCount = !notification.read ? 1 : 0;
        return res.addCallbacks({
            onAfterSuccess: async () => {
                await updateUnreadCountAfterRead(currentUnreadCount, state);
            },
        });
    }, 'markAsRead');
    /** Mark specific notifications as read (by ID). */
    NotificationStore.markManyAsRead = moduleBuilder.dispatch(async ({ state }, { notificationIds, }) => {
        const response = await client.p.notification.patchAll({
            requestBody: {
                notifications: notificationIds.map(id => ({
                    id,
                    read: true,
                })),
            },
        });
        const allNotifications = state.notifications.data ?? [];
        const currentUnreadCount = allNotifications.filter(n => notificationIds.includes(n.id) && !n.read).length;
        response.body.forEach(notification => commitNotificationUpdate({ notification }));
        return response.addCallbacks({
            onAfterSuccess: async () => {
                await updateUnreadCountAfterRead(currentUnreadCount, state);
            },
        });
    }, 'markManyAsRead');
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        NotificationStore.commitClearNotifications();
    });
})(NotificationStore || (NotificationStore = {}));
